import SvgIcon from "@mui/material/SvgIcon";
import {
  NotificationImportant as AdvisoryIcon,
  Check as CheckIcon,
  BugReport as CveIcon,
  Dns as DnsIcon,
  Policy as PolicyIcon,
} from "@mui/icons-material";
import { SxProps } from "@mui/material";
import { TailscaleIcon } from "~/components/icons/mondoo";
import { Icon_Ids } from "~/operations";
import {
  AdobeIcon,
  AdobeAcrobatIcon,
  AdobeIllustratorIcon,
  AdobeInDesignIcon,
  AdobePhotoshopIcon,
  AdobePremiereIcon,
  AixIcon,
  AlmaLinuxIcon,
  AlpineLinuxIcon,
  AppleIcon,
  AristaIcon,
  AtlassianIcon,
  AwsIcon,
  AzureIcon,
  BitwardenIcon,
  CentosIcon,
  ChromeIcon,
  CiscoIcon,
  ContainersIcon,
  DebianIcon,
  DomainsHostsIcon,
  DotNetIcon,
  DropboxIcon,
  FedoraIcon,
  FortiOsIcon,
  GardenLinuxIcon,
  GcpIcon,
  GimpIcon,
  GithubIcon,
  GithubDesktopIcon,
  GitlabIcon,
  GoogleWorkspaceIcon,
  IacIcon,
  LibreOfficeIcon,
  MondooIcon,
  KubernetesIcon,
  LinuxIcon,
  MacosIcon,
  MsExcelIcon,
  MsPowerPointIcon,
  MsSqlIcon,
  MsWordIcon,
  MozillaIcon,
  Ms365Icon,
  MsEdgeIcon,
  MsTeamsIcon,
  NetworkDevicesIcon,
  NmapIcon,
  OciIcon,
  OktaIcon,
  OperatingSystemsIcon,
  PanOsIcon,
  PythonIcon,
  RedhatIcon,
  RockyLinuxIcon,
  SafariIcon,
  SlackIcon,
  SuseIcon,
  TerraformIcon,
  UbuntuIcon,
  VlcMediaPlayerIcon,
  VmwareIcon,
  VmwareFusionIcon,
  VscodeIcon,
  WindowsIcon,
  WireSharkIcon,
  DockerIcon,
  WebexIcon,
  SevenZipIcon,
  NotepadPlusPlusIcon,
  CveOrgIcon,
  OpenEulerIcon,
  OracleJavaIcon,
  HuaweiIcon,
  TeamviewerIcon,
} from "~/icons";

type DynamicIconProps = {
  iconId: Icon_Ids | null | undefined;
  size?: number;
  defaultIcon?: typeof SvgIcon;
  sx?: SxProps;
};

export const DynamicIcon = ({
  iconId,
  size,
  defaultIcon = AdvisoryIcon,
  sx = {},
}: DynamicIconProps) => {
  const commonProps = {
    "data-testid": "dynamic-icon",
    sx: {
      fontSize: size || 24,
      ...sx,
    },
  };

  const iconKey = iconId || Icon_Ids.Default;

  const iconsDictionary: Partial<
    Record<Icon_Ids, typeof SvgIcon | React.ComponentType>
  > = {
    [Icon_Ids.Adobe]: AdobeIcon,
    [Icon_Ids.AdobeIllustrator]: AdobeIllustratorIcon,
    [Icon_Ids.AdobeIndesign]: AdobeInDesignIcon,
    [Icon_Ids.AdobePhotoshop]: AdobePhotoshopIcon,
    [Icon_Ids.AdobePremierePro]: AdobePremiereIcon,
    [Icon_Ids.AdobeReader]: AdobeAcrobatIcon,
    [Icon_Ids.CisaKev]: defaultIcon,
    [Icon_Ids.Cisco]: CiscoIcon,
    [Icon_Ids.Check]: CheckIcon,
    [Icon_Ids.Containers]: ContainersIcon,
    [Icon_Ids.Cve]: CveIcon,
    [Icon_Ids.CveOrg]: CveOrgIcon,
    [Icon_Ids.DomainsAndHosts]: DomainsHostsIcon,
    [Icon_Ids.FortiClient]: FortiOsIcon,
    [Icon_Ids.Fortios]: FortiOsIcon,
    [Icon_Ids.GardenLinux]: GardenLinuxIcon,
    [Icon_Ids.Gimp]: GimpIcon,
    [Icon_Ids.GoogleChrome]: ChromeIcon,
    [Icon_Ids.Iac]: IacIcon,
    [Icon_Ids.Metasploit]: defaultIcon,
    [Icon_Ids.Mozilla]: MozillaIcon,
    [Icon_Ids.MozillaFirefox]: MozillaIcon,
    [Icon_Ids.Microsoft]: WindowsIcon,
    [Icon_Ids.MicrosoftEdge]: MsEdgeIcon,
    [Icon_Ids.MicrosoftVisualStudioCode]: VscodeIcon,
    [Icon_Ids.MicrosoftExcel]: MsExcelIcon,
    [Icon_Ids.MicrosoftPowerpoint]: MsPowerPointIcon,
    [Icon_Ids.MicrosoftSqlServer]: MsSqlIcon,
    [Icon_Ids.MicrosoftTeams]: MsTeamsIcon,
    [Icon_Ids.MicrosoftWord]: MsWordIcon,
    [Icon_Ids.Mondoo]: MondooIcon,
    [Icon_Ids.NistNvd]: defaultIcon,
    [Icon_Ids.NetworkDevices]: NetworkDevicesIcon,
    [Icon_Ids.Nmap]: NmapIcon,
    [Icon_Ids.OperatingSystem]: OperatingSystemsIcon,
    [Icon_Ids.Panos]: PanOsIcon,
    [Icon_Ids.Ubuntu]: UbuntuIcon,
    [Icon_Ids.Redhat]: RedhatIcon,
    [Icon_Ids.Suse]: SuseIcon,
    [Icon_Ids.Debian]: DebianIcon,
    [Icon_Ids.Amazon]: AwsIcon,
    [Icon_Ids.Fedora]: FedoraIcon,
    [Icon_Ids.Centos]: CentosIcon,
    [Icon_Ids.Oracle]: OciIcon,
    [Icon_Ids.OracleJava]: OracleJavaIcon,
    [Icon_Ids.Other]: defaultIcon,
    [Icon_Ids.Policy]: PolicyIcon,
    [Icon_Ids.Risk]: defaultIcon,
    [Icon_Ids.Vmware]: VmwareIcon,
    [Icon_Ids.VmwarePhoton]: VmwareIcon,
    [Icon_Ids.VmwareTools]: VmwareIcon,
    [Icon_Ids.VmwareFusion]: VmwareFusionIcon,
    [Icon_Ids.Alpine]: AlpineLinuxIcon,
    [Icon_Ids.MicrosoftDotnet]: DotNetIcon,
    [Icon_Ids.Aix]: AixIcon,
    [Icon_Ids.Aws]: AwsIcon,
    [Icon_Ids.Azure]: AzureIcon,
    [Icon_Ids.Dns]: DnsIcon,
    [Icon_Ids.Dropbox]: DropboxIcon,
    [Icon_Ids.Gcp]: GcpIcon,
    [Icon_Ids.Github]: GithubIcon,
    [Icon_Ids.GithubDesktop]: GithubDesktopIcon,
    [Icon_Ids.Gitlab]: GitlabIcon,
    [Icon_Ids.GoogleWorkspace]: GoogleWorkspaceIcon,
    [Icon_Ids.K8S]: KubernetesIcon,
    [Icon_Ids.Linux]: LinuxIcon,
    [Icon_Ids.Macos]: MacosIcon,
    [Icon_Ids.Ms365]: Ms365Icon,
    [Icon_Ids.Oci]: OciIcon,
    [Icon_Ids.Okta]: OktaIcon,
    [Icon_Ids.Python]: PythonIcon,
    [Icon_Ids.RockyLinux]: RockyLinuxIcon,
    [Icon_Ids.Slack]: SlackIcon,
    [Icon_Ids.Terraform]: TerraformIcon,
    [Icon_Ids.Tailscale]: TailscaleIcon,
    [Icon_Ids.Teamviewer]: TeamviewerIcon,
    [Icon_Ids.Windows]: WindowsIcon,
    [Icon_Ids.AlmaLinux]: AlmaLinuxIcon,
    [Icon_Ids.Apple]: AppleIcon,
    [Icon_Ids.Safari]: SafariIcon,
    [Icon_Ids.Arch]: defaultIcon,
    [Icon_Ids.Arista]: AristaIcon,
    [Icon_Ids.Asset]: defaultIcon,
    [Icon_Ids.Atlassian]: AtlassianIcon,
    [Icon_Ids.Bitwarden]: BitwardenIcon,
    [Icon_Ids.Busybox]: defaultIcon,
    [Icon_Ids.Equinix]: defaultIcon,
    [Icon_Ids.EuroLinux]: defaultIcon,
    [Icon_Ids.Gentoo]: defaultIcon,
    [Icon_Ids.Ipmi]: defaultIcon,
    [Icon_Ids.Kali]: defaultIcon,
    [Icon_Ids.LibreOffice]: LibreOfficeIcon,
    [Icon_Ids.LinuxMint]: defaultIcon,
    [Icon_Ids.Manjaro]: defaultIcon,
    [Icon_Ids.Opcua]: defaultIcon,
    [Icon_Ids.Openwrt]: defaultIcon,
    [Icon_Ids.Pop]: defaultIcon,
    [Icon_Ids.Raspbian]: defaultIcon,
    [Icon_Ids.ScientificLinux]: defaultIcon,
    [Icon_Ids.Vcd]: defaultIcon,
    [Icon_Ids.VlcMediaPlayer]: VlcMediaPlayerIcon,
    [Icon_Ids.WrLinux]: defaultIcon,
    [Icon_Ids.Wireshark]: WireSharkIcon,
    [Icon_Ids.Default]: defaultIcon,
    [Icon_Ids.DockerDesktop]: DockerIcon,
    [Icon_Ids.NotepadPlusPlus]: NotepadPlusPlusIcon,
    [Icon_Ids.SevenZip]: SevenZipIcon,
    [Icon_Ids.Webex]: WebexIcon,
    [Icon_Ids.Windows_10]: WindowsIcon,
    [Icon_Ids.Windows_11]: WindowsIcon,
    [Icon_Ids.Huawei]: HuaweiIcon,
    [Icon_Ids.Openeuler]: OpenEulerIcon,
    [Icon_Ids.Sbom]: defaultIcon,
  };

  let Icon = iconsDictionary[iconKey] || defaultIcon;

  return <Icon {...commonProps} />;
};
